import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, CheckCircle, XCircle, Key } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre_empresa: '',
    rif_empresa: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('Empresa creada exitosamente');
      fetchUsers();
      setIsDialogOpen(false);
      setFormData({ email: '', password: '', nombre_empresa: '', rif_empresa: '' });
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear empresa');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success(`Usuario ${response.data.activo ? 'activado' : 'desactivado'}`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user:', error);
      toast.error('Error al cambiar estado del usuario');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Ingresa una nueva contraseña');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    try {
      await axios.put(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success(`Contraseña actualizada para ${selectedUser.email}`);
      setIsPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error al resetear contraseña');
    }
  };

  const openPasswordDialog = (usuario) => {
    setSelectedUser(usuario);
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  return (
    <div className="p-8" data-testid="admin-panel-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">Panel de Administración</h1>
          <p className="text-sm text-slate-600 mt-2">Gestión de empresas registradas</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-user-btn"
              className="bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg rounded-md px-6 py-2 font-bold tracking-wide"
            >
              <Plus size={20} className="mr-2" />
              Nueva Empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Crear Nueva Empresa</DialogTitle>
              <DialogDescription>
                Crea una cuenta para que una empresa use RetenGO
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nombre_empresa">Nombre de la Empresa *</Label>
                <Input
                  id="nombre_empresa"
                  data-testid="user-nombre-input"
                  value={formData.nombre_empresa}
                  onChange={(e) => setFormData({ ...formData, nombre_empresa: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="rif_empresa">RIF de la Empresa *</Label>
                <Input
                  id="rif_empresa"
                  data-testid="user-rif-input"
                  value={formData.rif_empresa}
                  onChange={(e) => setFormData({ ...formData, rif_empresa: e.target.value })}
                  placeholder="J-12345678-9"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email de Acceso *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="user-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contacto@empresa.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña *</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="user-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  data-testid="user-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="user-submit-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  Crear Empresa
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Empresa</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">RIF</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                <th className="p-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rol</th>
                <th className="p-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Estado</th>
                <th className="p-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-slate-500">
                    No hay empresas registradas
                  </td>
                </tr>
              ) : (
                users.map((usuario) => (
                  <tr
                    key={usuario.id}
                    data-testid={`user-row-${usuario.id}`}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="p-4 text-sm text-slate-900 font-medium">{usuario.nombre_empresa}</td>
                    <td className="p-4 font-mono text-sm text-slate-700">{usuario.rif_empresa}</td>
                    <td className="p-4 text-sm text-slate-600">{usuario.email}</td>
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                        usuario.rol === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {usuario.rol === 'admin' ? 'Admin' : 'Empresa'}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {usuario.activo ? (
                        <CheckCircle className="inline text-green-600" size={20} />
                      ) : (
                        <XCircle className="inline text-red-600" size={20} />
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          data-testid={`reset-password-${usuario.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => openPasswordDialog(usuario)}
                          className="border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <Key size={14} className="mr-1" />
                          Contraseña
                        </Button>
                        {usuario.rol !== 'admin' && (
                          <Button
                            data-testid={`toggle-user-${usuario.id}`}
                            size="sm"
                            variant={usuario.activo ? 'destructive' : 'default'}
                            onClick={() => handleToggleActive(usuario.id)}
                          >
                            {usuario.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog para resetear contraseña */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Resetear Contraseña</DialogTitle>
            <DialogDescription>
              {selectedUser && (
                <span>Cambiar contraseña para <strong>{selectedUser.email}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="new_password">Nueva Contraseña *</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPasswordDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleResetPassword}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Guardar Contraseña
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;